import { Icon } from '../../ui/Icon.js';

/**
 * prompt → [5 engines] → responses → mentions / citations.
 *
 * A horizontal pipeline: one prompt feeds five engine chips; their responses
 * are inspected for whether the brand was mentioned and/or cited. Spec 234
 * retires engine colour: the five engines are named right here in the
 * figure, so the colour was decoration standing in for a label that was
 * already on screen. Illustrative only.
 */
const ENGINES = [
  { name: 'ChatGPT' },
  { name: 'Gemini' },
  { name: 'Claude' },
  { name: 'Google AIO' },
  { name: 'Mistral' },
] as const;

export function CollectDiagram() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        {/* prompt */}
        {/* Spec 250 (review P1-15): the prompt is the inverted (ink) node of
            the Flow diagram recipe, not a Skagen block; the arrow is its edge. */}
        <div className="hub-flow__node shrink-0 text-center" aria-current="step">
          <span className="hub-flow__name">Your prompt</span>
        </div>
        <span aria-hidden="true" className="hub-flow__edge hidden sm:block" />
        {/* engines */}
        <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-5">
          {ENGINES.map((e) => (
            <div
              key={e.name}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-[var(--hub-color-line)] bg-[var(--hub-color-surface)] px-2 py-3 text-center text-xs font-medium text-[var(--hub-color-ink)]"
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-[var(--hub-color-ink-subtle)]"
                aria-hidden="true"
              />
              {e.name}
            </div>
          ))}
        </div>
      </div>

      {/* responses -> what we record */}
      <div className="rounded-xl border border-[var(--hub-color-line)] bg-[var(--hub-color-surface-sunken)] p-4">
        <p className="mb-3 text-center text-xs font-medium text-[var(--hub-color-ink-muted)]">
          For every response, GEO Observatory records
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Recorded icon={<Icon name="bx-at" />} label="Mentioned?" hint="Did your brand come up?" />
          <Recorded icon={<Icon name="bx-comment-detail" />} label="Cited?" hint="Was your site linked?" />
          <Recorded
            icon={<span className="text-sm font-semibold">∑</span>}
            label="Sources"
            hint="What it retrieved"
          />
        </div>
      </div>
    </div>
  );
}

function Recorded({ icon, label, hint }: { icon: React.ReactNode; label: string; hint: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[var(--hub-color-line)] bg-[var(--hub-color-surface)] px-3 py-2.5">
      <span className="hub-disc shrink-0">
        {icon}
      </span>
      <span>
        <span className="block text-sm font-semibold text-[var(--hub-color-ink)]">{label}</span>
        <span className="block text-xs text-[var(--hub-color-ink-subtle)]">{hint}</span>
      </span>
    </div>
  );
}
